import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Check, X } from 'lucide-react';
import { adminFetch, fmtDate, fmtNum } from './api';
import { PageHeader, ErrorBlock, StatusPill } from './widgets';

const TS_FIELDS = [
  ['created_at',        'Created'],
  ['updated_at',        'Last updated'],
  ['enriched_at',       'Enriched'],
  ['last_rating_attempt', 'Rating attempted'],
  ['cold_outreach_at',  'Cold sent'],
  ['cold_followup_at',  'Follow-up sent'],
  ['cold_replied_at',   'Replied'],
  ['bounced_at',        'Bounced'],
  ['unsubscribed_at',   'Unsubscribed'],
];

export default function AdminProspectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await adminFetch(`/api/admin-stats/prospects/${id}`);
      setData(d);
      document.title = `Sudco Admin — ${d.prospect.business_name}`;
    } catch (err) {
      setError(err);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleReplied = async () => {
    const replied = !data.prospect.cold_replied_at;
    setBusy(true);
    try {
      const d = await adminFetch(`/api/admin-stats/prospects/${id}/replied`, {
        method: 'PATCH',
        body: { replied },
      });
      setData((prev) => ({ ...prev, prospect: d.prospect }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorBlock error={error} onRetry={load} />;
  if (!data) return <div className="text-text-muted">Loading…</div>;

  const p = data.prospect;
  const stage = p.cold_replied_at ? 'replied'
    : p.bounced_at ? 'bounced'
    : p.unsubscribed_at ? 'unsubscribed'
    : p.cold_followup_at ? 'followup'
    : p.cold_outreach_at ? 'sent'
    : 'neutral';

  return (
    <>
      <Link to="/admin/prospects" className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text mb-4">
        <ChevronLeft size={14} /> All prospects
      </Link>

      <PageHeader
        title={p.business_name}
        subtitle={[p.industry, p.location].filter(Boolean).join(' · ')}
        right={<StatusPill tone={stage}>{stage}</StatusPill>}
      />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-xl border border-border bg-surface p-5">
          <h2 className="font-display font-semibold mb-3 text-text">Contact</h2>
          <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
            <dt className="text-text-muted">Email</dt>
            <dd className="text-text">{p.contact_email || '—'}</dd>
            <dt className="text-text-muted">Phone</dt>
            <dd className="text-text">{p.contact_phone || '—'}</dd>
            <dt className="text-text-muted">Website</dt>
            <dd className="text-text truncate">
              {p.current_website
                ? <a className="hover:text-accent" href={p.current_website} target="_blank" rel="noreferrer">{p.current_website}</a>
                : '—'}
            </dd>
            <dt className="text-text-muted">Rating</dt>
            <dd className="text-text">
              {p.rating != null ? `${p.rating} (${fmtNum(p.review_count)})` : '—'}
            </dd>
          </dl>

          <h2 className="font-display font-semibold mt-6 mb-3 text-text">Pipeline</h2>
          <dl className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-sm">
            {TS_FIELDS.map(([f, label]) => (
              <React.Fragment key={f}>
                <dt className="text-text-muted">{label}</dt>
                <dd className="text-text">{fmtDate(p[f])}</dd>
              </React.Fragment>
            ))}
          </dl>

          <button
            onClick={toggleReplied}
            disabled={busy}
            className={`mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-colors ${
              p.cold_replied_at
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                : 'border-border bg-surface-2 text-text hover:bg-surface'
            } disabled:opacity-60`}
          >
            {p.cold_replied_at
              ? <><X size={14} /> Mark not replied</>
              : <><Check size={14} /> Mark replied</>}
          </button>
        </div>

        <div className="rounded-xl border border-border bg-surface p-5">
          <h2 className="font-display font-semibold mb-3 text-text">
            Showcase clicks
            <span className="ml-2 text-xs text-text-muted font-normal">{fmtNum(data.click_count)} total</span>
          </h2>
          {data.recent_clicks.length === 0 ? (
            <div className="text-sm text-text-muted">No clicks yet.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.recent_clicks.map((c) => (
                <li key={c.id} className="border-b border-border pb-2 last:border-0">
                  <div className="text-text">{c.demo_slug}</div>
                  <div className="text-xs text-text-muted">{fmtDate(c.viewed_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
